import axios from 'axios';

// Use /api which works with proxy in dev and direct path in production
const API_BASE_URL = process.env.VUE_APP_API_URL || '/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor: auth token + organisation pour le panel membre
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    let newsSessionId = localStorage.getItem('newsSessionId');
    if (!newsSessionId) {
      newsSessionId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
      localStorage.setItem('newsSessionId', newsSessionId);
    }
    if ((config.url || '').startsWith('/news/')) {
      config.headers['X-News-Session-Id'] = newsSessionId;
    }
    const url = config.url || '';
    if (url.includes('/member') && !url.includes('/auth/')) {
      const orgId = localStorage.getItem('memberOrganizationId');
      if (orgId) {
        config.headers['X-Organization-Id'] = orgId;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const requestUrl = error.config?.url || '';
      // Don't redirect on login/auth endpoints - let components handle the error
      const isAuthEndpoint =
        requestUrl.includes('/auth/login') ||
        requestUrl.includes('/auth/admin/login') ||
        requestUrl.includes('/auth/member/login') ||
        requestUrl.includes('/auth/partner/login') ||
        requestUrl.includes('/auth/user/request-otp') ||
        requestUrl.includes('/auth/user/verify-otp');

      if (!isAuthEndpoint) {
        // Unauthorized on protected routes - clear token and redirect to login
        localStorage.removeItem('authToken');
        localStorage.removeItem('isAuthenticated');
        const path = window.location.pathname;
        const isAdminRoute = path.startsWith('/admin');
        const isMemberRoute = path.startsWith('/member');
        const isPartnerRoute = path.startsWith('/partner');
        if (isAdminRoute) window.location.href = '/admin/login';
        else if (isMemberRoute) window.location.href = '/member/login';
        else if (isPartnerRoute) window.location.href = '/partner/login';
        else window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  adminLogin: (credentials) => api.post('/auth/admin/login', credentials),
  memberLogin: (credentials) => api.post('/auth/member/login', credentials),
  partnerLogin: (credentials) => api.post('/auth/partner/login', credentials),
  userRequestOtp: (phone, options = {}) =>
    api.post('/auth/user/request-otp', { phone, register: options.register }),
  userVerifyOtp: (phone, otp, profile = {}) =>
    api.post('/auth/user/verify-otp', { phone, otp, ...profile }),
};

export const STAFF_FUNCTION_LABELS = {
  secretaire: 'Secrétaire',
  comptable: 'Comptable',
  surveillant: 'Surveillant',
  bibliothecaire: 'Bibliothécaire',
  censeur: 'Censeur',
  intendant: 'Intendant',
  fonde_pouvoir: 'Fondé de pouvoir',
  autre: 'Autre',
};

// Admin Accounts API
export const adminAccountsAPI = {
  getAll: (search) => api.get('/admin-accounts', { params: { search } }),
  getById: (id) => api.get(`/admin-accounts/${id}`),
  create: (data) => api.post('/admin-accounts', data),
  update: (id, data) => api.put(`/admin-accounts/${id}`, data),
  delete: (id) => api.delete(`/admin-accounts/${id}`),
  changePassword: (id, password) => api.put(`/admin-accounts/${id}/password`, { password }),
};

// Schools API (Public)
export const schoolsAPI = {
  getAll: (params = {}) => api.get('/schools', { params }),
  getBySlug: (slug) => api.get(`/schools/${slug}`),
  getFavorites: () => api.get('/schools/favorites/all'),
  addFavorite: (id) => api.post(`/schools/${id}/favorite`),
  removeFavorite: (id) => api.delete(`/schools/${id}/favorite`),
};

// Admin Schools API
export const adminSchoolsAPI = {
  getAll: (params = {}) => api.get('/admin/schools', { params }),
  getById: (id) => api.get(`/admin/schools/${id}`),
  create: (data) => api.post('/admin/schools', data),
  update: (id, data) => api.put(`/admin/schools/${id}`, data),
  delete: (id) => api.delete(`/admin/schools/${id}`),
  regenerateQRCode: (id) => api.post(`/admin/schools/${id}/qr-code`),
  exportPdf: (params = {}) => api.get('/admin/schools/export-pdf', { params, responseType: 'blob' }),
  exportExcel: (params = {}) => api.get('/admin/schools/export-excel', { params, responseType: 'blob' }),
};

// Admin Countries API
export const adminCountriesAPI = {
  getAll: (params = {}) => api.get('/admin/countries', { params }),
  getById: (id) => api.get(`/admin/countries/${id}`),
  create: (data) => api.post('/admin/countries', data),
  update: (id, data) => api.put(`/admin/countries/${id}`, data),
  delete: (id) => api.delete(`/admin/countries/${id}`),
};

// Public Countries API (for filters)
export const countriesAPI = {
  getAll: (params = {}) => api.get('/admin/countries', { params: { ...params, activeOnly: 'true' } }),
};

// Classrooms API (admin settings for classes / levels)
export const classroomsAPI = {
  getAll: () => api.get('/classrooms'),
};

// Admin Users API
export const adminUsersAPI = {
  getCount: () => api.get('/admin/users/count'),
  getAll: (params = {}) => api.get('/admin/users', { params }),
  delete: (id) => api.delete(`/admin/users/${id}`),
};

export const adminTrainingsAPI = {
  getStats: () => api.get('/admin/trainings/stats'),
  listCategories: () => api.get('/admin/trainings/categories'),
  createCategory: (data) => api.post('/admin/trainings/categories', data),
  updateCategory: (id, data) => api.put(`/admin/trainings/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/admin/trainings/categories/${id}`),
  listRequests: (params = {}) => api.get('/admin/trainings/requests', { params }),
  updateRequest: (id, data) => api.put(`/admin/trainings/requests/${id}`, data),
  getTracking: () => api.get('/admin/trainings/tracking'),
  listTrainings: (params = {}) => api.get('/admin/trainings', { params }),
  getTraining: (id) => api.get(`/admin/trainings/${id}`),
  getTrainingStats: (id) => api.get(`/admin/trainings/${id}/stats`),
  previewTraining: (id) => api.get(`/admin/trainings/${id}`),
  exportTrainingJson: (id) => api.get(`/admin/trainings/${id}/export-json`, { responseType: 'blob' }),
  exportTrainingPdf: (id) => api.get(`/admin/trainings/${id}/export-pdf`, { responseType: 'blob' }),
  createTraining: (data) => api.post('/admin/trainings', data),
  updateTraining: (id, data) => api.put(`/admin/trainings/${id}`, data),
  deleteTraining: (id) => api.delete(`/admin/trainings/${id}`),
};

// Users API (app user - WhatsApp OTP)
export const usersAPI = {
  getProfile: () => api.get('/users/me'),
  updateProfile: (data) => api.put('/users/me', data),
  getConnectedServices: () => api.get('/users/me/connected-services'),
  getMySchools: () => api.get('/users/me/schools'),
  getMySchool: (schoolId) => api.get(`/users/me/schools/${schoolId}`),
  addSchoolByCode: (shortCode) => api.post('/users/me/schools', { shortCode }),
  createMySchool: (data) => api.post('/users/me/schools/create', data),
  updateMySchool: (schoolId, data) => api.put(`/users/me/schools/${schoolId}`, data),
  getSchoolServices: (schoolId) => api.get(`/users/me/schools/${schoolId}/services`),
  getSchoolPayments: (schoolId, params) => api.get(`/users/me/schools/${schoolId}/payments`, { params }),
  assignPaymentCarteScolaire: (schoolId, paymentId, data) =>
    api.put(`/users/me/schools/${schoolId}/payments/${paymentId}/assign-carte-scolaire`, data),
  getSchoolStudents: (schoolId) => api.get(`/users/me/schools/${schoolId}/students`),
  createSchoolStudent: (schoolId, data) => api.post(`/users/me/schools/${schoolId}/students`, data),
  updateSchoolStudent: (schoolId, studentId, data) => api.put(`/users/me/schools/${schoolId}/students/${studentId}`, data),
  deleteSchoolStudent: (schoolId, studentId) => api.delete(`/users/me/schools/${schoolId}/students/${studentId}`),
  importSchoolStudents: (schoolId, rows) => api.post(`/users/me/schools/${schoolId}/students/import`, { rows }),
  exportCarteScolairePdf: (schoolId) =>
    api.get(`/users/me/schools/${schoolId}/students/export-carte-scolaire-pdf`, { responseType: 'blob' }),
  importSchoolPhotos: (schoolId, files) => {
    const formData = new FormData();
    const list = Array.isArray(files) ? files : (files ? [files] : []);
    list.forEach((f) => {
      if (f) formData.append('photos', f);
    });
    return api.post(`/users/me/schools/${schoolId}/students/import-photos-files`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export const trainingsAPI = {
  getFeaturedHome: (params = {}) => api.get('/trainings/featured/home', { params }),
  getCategories: () => api.get('/trainings/categories'),
  getAll: (params = {}) => api.get('/trainings', { params }),
  getBySlug: (slug) => api.get(`/trainings/${encodeURIComponent(slug)}`),
  submitQcm: (id, answers) => api.post(`/trainings/${id}/submit-qcm`, { answers }),
  completeStep: (trainingId, stepId, answers = {}) =>
    api.post(`/trainings/${trainingId}/steps/${stepId}/complete`, { answers }),
  getMyPath: () => api.get('/trainings/my-path'),
  getBadges: () => api.get('/trainings/badges'),
  createRequest: (data) => api.post('/trainings/requests', data),
};

// Admin Organizations API
export const organizationsAPI = {
  getAll: (params) => api.get('/admin/organizations', { params }),
  getById: (id) => api.get(`/admin/organizations/${id}`),
  create: (data) => api.post('/admin/organizations', data),
  update: (id, data) => api.put(`/admin/organizations/${id}`, data),
  delete: (id) => api.delete(`/admin/organizations/${id}`),
};

// Admin Services API
export const servicesAPI = {
  getAll: (params) => api.get('/admin/services', { params }),
  getById: (id) => api.get(`/admin/services/${id}`),
  create: (data) => api.post('/admin/services', data),
  update: (id, data) => api.put(`/admin/services/${id}`, data),
  delete: (id) => api.delete(`/admin/services/${id}`),
  getPayments: (id) => api.get(`/admin/services/${id}/payments`),
  getPartnerPackageSchools: (serviceId) =>
    api.get(`/admin/services/${serviceId}/partner-package-schools`),
  buildPartnerPackage: (serviceId, schoolId) =>
    api.post(`/admin/services/${serviceId}/partner-packages/build`, { schoolId }),
  getPartnerPackageJob: (serviceId, jobId) =>
    api.get(`/admin/services/${serviceId}/partner-packages/jobs/${jobId}`),
};

// Admin Schools – students (carte scolaire)
export const adminSchoolsStudentsAPI = {
  list: (schoolId) => api.get(`/admin/schools/${schoolId}/students`),
  create: (schoolId, data) => api.post(`/admin/schools/${schoolId}/students`, data),
  update: (schoolId, studentId, data) => api.put(`/admin/schools/${schoolId}/students/${studentId}`, data),
  delete: (schoolId, studentId) => api.delete(`/admin/schools/${schoolId}/students/${studentId}`),
  import: (schoolId, rows) => api.post(`/admin/schools/${schoolId}/students/import`, { rows }),
};

// Admin Payments API
export const adminPaymentsAPI = {
  list: (params) => api.get('/admin/payments', { params }),
  listByService: (serviceId) => api.get(`/admin/services/${serviceId}/payments`),
};

// Admin Dashboard API
export const adminDashboardAPI = {
  getOverview: (params = {}) => api.get('/admin/dashboard/overview', { params }),
};

// Admin Partners API
export const partnersAPI = {
  getAll: (params) => api.get('/admin/partners', { params }),
  getById: (id) => api.get(`/admin/partners/${id}`),
  create: (data) => api.post('/admin/partners', data),
  update: (id, data) => api.put(`/admin/partners/${id}`, data),
  delete: (id) => api.delete(`/admin/partners/${id}`),
};

// Payment API (public - no auth required for these)
export const paymentAPI = {
  getBySchoolCode: (code) => api.get(`/payment/public/by-school-code/${encodeURIComponent(code)}`),
  getByServiceCode: (code) => api.get(`/payment/public/by-service-code/${encodeURIComponent(code)}`),
  searchSchools: (q) => api.get('/payment/public/search-schools', { params: { q } }),
  create: (data) => api.post('/payment/create', data),
  getStatus: (reference) => api.get(`/payment/status/${encodeURIComponent(reference)}`),
  getReceipt: (reference) => api.get(`/payment/receipt/${encodeURIComponent(reference)}`),
};

// Public votes API
export const votesAPI = {
  getPublicCampaign: (slug) => api.get(`/votes/public/${encodeURIComponent(slug)}`),
  getPublicResults: (slug) => api.get(`/votes/public/${encodeURIComponent(slug)}/results`),
  getMyStats: (slug, phone) => api.get(`/votes/public/${encodeURIComponent(slug)}/my-stats`, { params: { phone } }),
  submitVote: (slug, data) => api.post(`/votes/public/${encodeURIComponent(slug)}/vote`, data),
};

// Push Notifications API
export const pushAPI = {
  subscribe: (data) => api.post('/push/subscribe', data),
  unsubscribe: (token) => api.delete('/push/subscribe', { data: { token } }),
};

// Partner API (partner panel - services, schools, students, traited)
export const partnerAPI = {
  getMe: () => api.get('/partner/me'),
  getDashboard: () => api.get('/partner/dashboard'),
  getServices: (params) => api.get('/partner/services', { params }),
  getServiceById: (serviceId) => api.get(`/partner/services/${serviceId}`),
  getServiceSchools: (serviceId) => api.get(`/partner/services/${serviceId}/schools`),
  getSchools: () => api.get('/partner/schools'),
  getSchoolStudents: (schoolId, serviceId) =>
    api.get(`/partner/schools/${schoolId}/students`, { params: serviceId ? { serviceId } : {} }),
  getSchoolStudentsExportExcel: (schoolId, serviceId) =>
    api.get(`/partner/schools/${schoolId}/students/export-excel`, {
      params: serviceId ? { serviceId } : {},
      responseType: 'blob',
    }),
  getSchoolStudentsPhotosZip: (schoolId, serviceId) =>
    api.get(`/partner/schools/${schoolId}/students/photos-zip`, {
      params: serviceId ? { serviceId } : {},
      responseType: 'blob',
    }),
  setStudentTraited: (studentId) => api.patch(`/partner/students/${studentId}/traited`),
  setSchoolStudentsTraited: (schoolId, serviceId) =>
    api.patch(`/partner/schools/${schoolId}/students/traited`, {}, {
      params: serviceId ? { serviceId } : {},
    }),
  votes: {
    listCampaigns: () => api.get('/votes/partner/campaigns'),
    createCampaign: (data) => api.post('/votes/partner/campaigns', data),
    getCampaign: (campaignId) => api.get(`/votes/partner/campaigns/${campaignId}`),
    updateCampaign: (campaignId, data) => api.put(`/votes/partner/campaigns/${campaignId}`, data),
    archiveCampaign: (campaignId) => api.delete(`/votes/partner/campaigns/${campaignId}`),
    listVotes: (campaignId, params) => api.get(`/votes/partner/campaigns/${campaignId}/votes`, { params }),
    createCandidate: (campaignId, data) => api.post(`/votes/partner/campaigns/${campaignId}/candidates`, data),
    updateCandidate: (campaignId, candidateId, data) =>
      api.put(`/votes/partner/campaigns/${campaignId}/candidates/${candidateId}`, data),
    deleteCandidate: (campaignId, candidateId) =>
      api.delete(`/votes/partner/campaigns/${campaignId}/candidates/${candidateId}`),
    exportResults: (campaignId) =>
      api.get(`/votes/partner/campaigns/${campaignId}/export.csv`, { responseType: 'blob' }),
  },
};

// Member API (organization members - scoped to their org)
export const memberAPI = {
  getMe: () => api.get('/member/me'),
  getDashboard: () => api.get('/member/dashboard'),
  payments: {
    list: (params) => api.get('/member/payments', { params }),
    listByService: (serviceId) => api.get(`/member/services/${serviceId}/payments`),
    create: (data) => api.post('/member/payments', data),
  },
  schools: {
    getAll: (params) => api.get('/member/schools', { params }),
    getById: (id) => api.get(`/member/schools/${id}`),
    create: (data) => api.post('/member/schools', data),
    update: (id, data) => api.put(`/member/schools/${id}`, data),
    delete: (id) => api.delete(`/member/schools/${id}`),
    importPreview: (rows) => api.post('/member/schools/import-preview', { rows }),
    import: (rows) => api.post('/member/schools/import', { rows }),
    regenerateQRCode: (id) => api.post(`/member/schools/${id}/qr-code`),
    exportPdf: (params = {}) => api.get('/member/schools/export-pdf', { params, responseType: 'blob' }),
    exportExcel: (params = {}) => api.get('/member/schools/export-excel', { params, responseType: 'blob' }),
    join: (schoolIds) => api.post('/member/schools/join', { schoolIds }),
    students: {
      list: (schoolId) => api.get(`/member/schools/${schoolId}/students`),
      create: (schoolId, data) => api.post(`/member/schools/${schoolId}/students`, data),
      update: (schoolId, studentId, data) => api.put(`/member/schools/${schoolId}/students/${studentId}`, data),
      delete: (schoolId, studentId) => api.delete(`/member/schools/${schoolId}/students/${studentId}`),
      import: (schoolId, rows) => api.post(`/member/schools/${schoolId}/students/import`, { rows }),
    },
  },
  services: {
    getAll: (params) => api.get('/member/services', { params }),
    getById: (id) => api.get(`/member/services/${id}`),
    create: (data) => api.post('/member/services', data),
    update: (id, data) => api.put(`/member/services/${id}`, data),
    delete: (id) => api.delete(`/member/services/${id}`),
  },
  partners: {
    getAll: (params) => api.get('/member/partners', { params }),
    getById: (id) => api.get(`/member/partners/${id}`),
  },
};

// Admin Members API (invite organization members)
export const adminMembersAPI = {
  getAll: (params) => api.get('/admin/members', { params }),
  getById: (id) => api.get(`/admin/members/${id}`),
  create: (data) => api.post('/admin/members', data),
  update: (id, data) => api.put(`/admin/members/${id}`, data),
  delete: (id) => api.delete(`/admin/members/${id}`),
};

// Patronat API
export const patronatAPI = {
  getMembers: (params = {}) => api.get('/patronat/members', { params }),
};

// Admin Patronat API
export const adminPatronatAPI = {
  getMembers: (params = {}) => api.get('/admin/patronat/members', { params }),
  getSettings: () => api.get('/admin/patronat/settings'),
  updateSettings: (data) => api.put('/admin/patronat/settings', data),
  getMemberById: (id) => api.get(`/admin/patronat/members/${id}`),
  createMember: (data) => api.post('/admin/patronat/members', data),
  updateMember: (id, data) => api.put(`/admin/patronat/members/${id}`, data),
  deleteMember: (id) => api.delete(`/admin/patronat/members/${id}`),
};


// News API (public – optional auth via Bearer for logged-only visibility)
export const newsAPI = {
  getFeatured: (params) => api.get('/news/featured/home', { params }),
  getCategories: () => api.get('/news/categories'),
  getPopularTags: (params) => api.get('/news/tags/popular', { params }),
  getAll: (params) => api.get('/news', { params }),
  getBySlug: (slug) => api.get(`/news/${encodeURIComponent(slug)}`),
  getRelated: (slug, params) => api.get(`/news/${encodeURIComponent(slug)}/related`, { params }),
  react: (id) => api.post(`/news/${id}/react`),
  unreact: (id) => api.delete(`/news/${id}/react`),
  share: (id) => api.post(`/news/${id}/share`),
};

// Admin News API
export const adminNewsAPI = {
  listAuthors: (params) => api.get('/admin/news/authors', { params }),
  createAuthor: (data) => api.post('/admin/news/authors', data),
  updateAuthor: (id, data) => api.put(`/admin/news/authors/${id}`, data),
  deleteAuthor: (id) => api.delete(`/admin/news/authors/${id}`),

  listCategories: () => api.get('/admin/news/categories'),
  createCategory: (data) => api.post('/admin/news/categories', data),
  updateCategory: (id, data) => api.put(`/admin/news/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/admin/news/categories/${id}`),

  listArticles: (params) => api.get('/admin/news/articles', { params }),
  getArticle: (id) => api.get(`/admin/news/articles/${id}`),
  createArticle: (data) => api.post('/admin/news/articles', data),
  updateArticle: (id, data) => api.put(`/admin/news/articles/${id}`, data),
  deleteArticle: (id) => api.delete(`/admin/news/articles/${id}`),
  previewArticle: (id) => api.get(`/admin/news/articles/${id}/preview`),
  updateWorkflow: (id, data) => api.post(`/admin/news/articles/${id}/workflow`, data),
  publishArticle: (id) => api.post(`/admin/news/articles/${id}/publish`),
  unpublishArticle: (id) => api.post(`/admin/news/articles/${id}/unpublish`),
  getAnalytics: () => api.get('/admin/news/analytics/overview'),
};

// Admin Notifications API
export const adminNotificationsAPI = {
  getStats: () => api.get('/admin/notifications/stats'),
  send: (data) => api.post('/admin/notifications/send', data),
};

// Upload API
export const uploadAPI = {
  uploadImage: (file, folder) => {
    const formData = new FormData();
    formData.append('image', file);
    if (folder) formData.append('folder', folder);
    return api.post('/upload/image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  /** PDF ou image (signature, cachet) */
  uploadDocument: (file, folder) => {
    const formData = new FormData();
    formData.append('document', file);
    if (folder) formData.append('folder', folder);
    return api.post('/upload/document', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  uploadMedia: (file, folder) => {
    const formData = new FormData();
    formData.append('media', file);
    if (folder) formData.append('folder', folder);
    return api.post('/upload/media', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  uploadImages: (files, folder) => {
    const formData = new FormData();
    files.forEach((file) => formData.append('images', file));
    if (folder) formData.append('folder', folder);
    return api.post('/upload/images', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};


export default api;
